import { z } from 'zod'

// ── Enums (string-unions, parallel aan Prisma) ──
export const ONGEVAL_STATUS = ['CONCEPT', 'IN_BEHANDELING', 'AFGEROND'] as const
export type OngevalStatus = (typeof ONGEVAL_STATUS)[number]

export const ONGEVAL_FASE = [
  'FASE_1_ONGEVAL',
  'FASE_2_KLANTCONTACT',
  'FASE_3_EVALUATIE_MEDEWERKER',
  'FASE_4_LANGDURIG_VERZUIM',
  'FASE_5_EINDEVALUATIE',
] as const
export type OngevalFase = (typeof ONGEVAL_FASE)[number]

export const ONGEVAL_STAP_ROL = ['TEAMLEIDER', 'HR'] as const
export type OngevalStapRol = (typeof ONGEVAL_STAP_ROL)[number]

export const ONGEVAL_SLACHTOFFER_TYPE = ['MEDEWERKER', 'UITZENDKRACHT', 'ZZP', 'STAGIAIR', 'OVERIG'] as const
export type OngevalSlachtofferType = (typeof ONGEVAL_SLACHTOFFER_TYPE)[number]

export const ONGEVAL_BIJLAGE_TYPE = ['PDF_REGISTER', 'KLANT_VERSLAGLEGGING', 'GESPREKSVERSLAG', 'OVERIG'] as const
export type OngevalBijlageType = (typeof ONGEVAL_BIJLAGE_TYPE)[number]

// ── Labels ──
export const ONGEVAL_STATUS_LABELS: Record<OngevalStatus, string> = {
  CONCEPT: 'Concept',
  IN_BEHANDELING: 'In behandeling',
  AFGEROND: 'Afgerond',
}
export const ONGEVAL_STATUS_BADGE: Record<OngevalStatus, 'info' | 'warning' | 'success'> = {
  CONCEPT: 'info',
  IN_BEHANDELING: 'warning',
  AFGEROND: 'success',
}
export const ONGEVAL_FASE_LABELS: Record<OngevalFase, string> = {
  FASE_1_ONGEVAL: '1. (Bijna-)ongeval',
  FASE_2_KLANTCONTACT: '2. Klantcontact',
  FASE_3_EVALUATIE_MEDEWERKER: '3. Evaluatie medewerker',
  FASE_4_LANGDURIG_VERZUIM: '4. Bij langdurig verzuim',
  FASE_5_EINDEVALUATIE: '5. Eindevaluatie',
}
export const ONGEVAL_STAP_ROL_LABELS: Record<OngevalStapRol, string> = {
  TEAMLEIDER: 'Teamleider',
  HR: 'HR',
}
export const ONGEVAL_SLACHTOFFER_TYPE_LABELS: Record<OngevalSlachtofferType, string> = {
  MEDEWERKER: 'Medewerker',
  UITZENDKRACHT: 'Uitzendkracht',
  ZZP: "Zzp'er",
  STAGIAIR: 'Stagiair',
  OVERIG: 'Overig',
}
export const ONGEVAL_BIJLAGE_TYPE_LABELS: Record<OngevalBijlageType, string> = {
  PDF_REGISTER: 'Registratie (PDF)',
  KLANT_VERSLAGLEGGING: 'Klant-verslaglegging',
  GESPREKSVERSLAG: 'Gespreksverslag',
  OVERIG: 'Overig',
}

// ── DTO's ──
export interface OngevalWorkflowStapDTO {
  id: string
  fase: OngevalFase
  volgorde: number
  rol: OngevalStapRol
  omschrijving: string
  isExtra: boolean
  afgevinkt: boolean
  afgevinktDoorNaam: string | null
  afgevinktOp: string | null
  notitie: string | null
}

export interface OngevalWorkflowStapSjabloonDTO {
  id: string
  fase: OngevalFase
  volgorde: number
  rol: OngevalStapRol
  omschrijving: string
  actief: boolean
}

export interface OngevalBijlageDTO {
  documentId: string
  versieNr: number
  bijlageType: OngevalBijlageType | null
  fileName: string
  fileSize: number
  mimeType: string
  uploadedByNaam: string | null
  uploadedAt: string
}

export interface OngevalAuditDTO {
  id: string
  actie: string
  actorNaam: string | null
  toelichting: string | null
  createdAt: string
}

export interface OngevalSickLeaveSamenvattingDTO {
  id: string
  status: string
  startDate: string
  endDate: string | null
}

export interface OngevalRegistratieListRowDTO {
  id: string
  referentienummer: string | null
  datumTijdOngeval: string
  slachtofferNaam: string | null
  slachtofferType: OngevalSlachtofferType
  locatie: string | null
  clientNaam: string | null
  status: OngevalStatus
  huidigeFase: OngevalFase
  meldingsplichtig: boolean
}

export interface OngevalRegistratieDetailDTO {
  id: string
  referentienummer: string | null
  entityId: string | null
  entityNaam: string | null
  ingevuldDoorNaam: string | null
  verzuim: boolean
  ziekenhuisopname: boolean
  blijvendLetsel: boolean
  overlijden: boolean
  meldingsplichtig: boolean
  arbeidsinspectieGemeld: boolean
  arbeidsinspectieGemeldOp: string | null
  datumTijdOngeval: string
  locatie: string | null
  clientId: string | null
  clientNaam: string | null
  slachtofferEmployeeId: string | null
  slachtofferType: OngevalSlachtofferType
  slachtofferNaam: string | null
  slachtofferFunctieRelatie: string | null
  letsel: string | null
  verzuimduur: string | null
  omschrijvingOngeval: string
  handelenNaOngeval: string | null
  klantInfo: string | null
  status: OngevalStatus
  huidigeFase: OngevalFase
  eindevaluatie: string | null
  afgerondOp: string | null
  afgerondDoorNaam: string | null
  sickLeaveId: string | null
  sickLeave: OngevalSickLeaveSamenvattingDTO | null
  meldingOnveiligeSituatieId: string | null
  stappen: OngevalWorkflowStapDTO[]
  bijlagen: OngevalBijlageDTO[]
  audits: OngevalAuditDTO[]
  createdAt: string
  updatedAt: string
}

// ── Zod request-schemas ──
const triageSchema = {
  verzuim: z.boolean().default(false),
  ziekenhuisopname: z.boolean().default(false),
  blijvendLetsel: z.boolean().default(false),
  overlijden: z.boolean().default(false),
}

export const createOngevalSchema = z
  .object({
    entityId: z.string().trim().min(1).optional().nullable(),
    ...triageSchema,
    arbeidsinspectieGemeld: z.boolean().default(false),
    arbeidsinspectieGemeldOp: z.string().datetime().optional().nullable(),
    datumTijdOngeval: z.string().datetime(),
    locatie: z.string().trim().max(300).optional().nullable(),
    clientId: z.string().trim().min(1).optional().nullable(),
    slachtofferEmployeeId: z.string().trim().min(1).optional().nullable(),
    slachtofferType: z.enum(ONGEVAL_SLACHTOFFER_TYPE).default('MEDEWERKER'),
    slachtofferNaam: z.string().trim().max(200).optional().nullable(),
    slachtofferFunctieRelatie: z.string().trim().max(200).optional().nullable(),
    letsel: z.string().trim().max(1000).optional().nullable(),
    verzuimduur: z.string().trim().max(100).optional().nullable(),
    omschrijvingOngeval: z.string().trim().min(1, 'Omschrijving is verplicht').max(10000),
    handelenNaOngeval: z.string().trim().max(10000).optional().nullable(),
    klantInfo: z.string().trim().max(10000).optional().nullable(),
    status: z.enum(['CONCEPT', 'IN_BEHANDELING']).default('CONCEPT'),
  })
  .refine((d) => d.slachtofferType !== 'MEDEWERKER' || !!d.slachtofferEmployeeId, {
    message: 'Kies een medewerker of een ander slachtoffertype',
    path: ['slachtofferEmployeeId'],
  })
  .refine((d) => d.slachtofferType === 'MEDEWERKER' || !!d.slachtofferNaam, {
    message: 'Naam slachtoffer is verplicht bij niet-medewerker',
    path: ['slachtofferNaam'],
  })

export const updateOngevalSchema = z.object({
  entityId: z.string().trim().min(1).nullable().optional(),
  verzuim: z.boolean().optional(),
  ziekenhuisopname: z.boolean().optional(),
  blijvendLetsel: z.boolean().optional(),
  overlijden: z.boolean().optional(),
  arbeidsinspectieGemeld: z.boolean().optional(),
  arbeidsinspectieGemeldOp: z.string().datetime().nullable().optional(),
  datumTijdOngeval: z.string().datetime().optional(),
  locatie: z.string().trim().max(300).nullable().optional(),
  clientId: z.string().trim().min(1).nullable().optional(),
  slachtofferEmployeeId: z.string().trim().min(1).nullable().optional(),
  slachtofferType: z.enum(ONGEVAL_SLACHTOFFER_TYPE).optional(),
  slachtofferNaam: z.string().trim().max(200).nullable().optional(),
  slachtofferFunctieRelatie: z.string().trim().max(200).nullable().optional(),
  letsel: z.string().trim().max(1000).nullable().optional(),
  verzuimduur: z.string().trim().max(100).nullable().optional(),
  omschrijvingOngeval: z.string().trim().min(1).max(10000).optional(),
  handelenNaOngeval: z.string().trim().max(10000).nullable().optional(),
  klantInfo: z.string().trim().max(10000).nullable().optional(),
  status: z.enum(['CONCEPT', 'IN_BEHANDELING']).optional(),
})

export const stapAfvinkSchema = z.object({
  afgevinkt: z.boolean(),
  notitie: z.string().trim().max(2000).optional().nullable(),
})

export const stapToevoegenSchema = z.object({
  fase: z.enum(ONGEVAL_FASE),
  rol: z.enum(ONGEVAL_STAP_ROL),
  omschrijving: z.string().trim().min(1).max(500),
})

export const afrondenSchema = z.object({
  eindevaluatie: z.string().trim().min(1, 'Eindevaluatie is verplicht').max(10000),
})

export const sjabloonStapSchema = z.object({
  fase: z.enum(ONGEVAL_FASE),
  rol: z.enum(ONGEVAL_STAP_ROL),
  omschrijving: z.string().trim().min(1).max(500),
  volgorde: z.number().int().min(0).optional(),
  actief: z.boolean().optional(),
})

export const bijlageTypeSchema = z.enum(ONGEVAL_BIJLAGE_TYPE)

// ── Pure helpers ──
export interface OngevalTriage {
  ziekenhuisopname: boolean
  blijvendLetsel: boolean
  overlijden: boolean
}

export function isArbeidsinspectieMeldingNodig(t: OngevalTriage): boolean {
  return t.ziekenhuisopname || t.blijvendLetsel || t.overlijden
}

export interface StapVoortgang {
  fase: OngevalFase
  afgevinkt: number
  totaal: number
}

export function faseVoortgang(
  stappen: ReadonlyArray<{ fase: OngevalFase; afgevinkt: boolean }>,
): StapVoortgang[] {
  return ONGEVAL_FASE.map((fase) => {
    const inFase = stappen.filter((s) => s.fase === fase)
    return { fase, afgevinkt: inFase.filter((s) => s.afgevinkt).length, totaal: inFase.length }
  })
}

export function berekenHuidigeFase(
  stappen: ReadonlyArray<{ fase: OngevalFase; afgevinkt: boolean }>,
): OngevalFase {
  for (const fase of ONGEVAL_FASE) {
    const inFase = stappen.filter((s) => s.fase === fase)
    if (inFase.length > 0 && inFase.some((s) => !s.afgevinkt)) return fase
  }
  return ONGEVAL_FASE[ONGEVAL_FASE.length - 1]
}
